import { Component, Element, Event, EventEmitter, Method, Prop, State, Watch, h } from '@stencil/core';

import Bind from 'bind-decorator';
import { ZdsInputElement } from '../../types';
import { AriaObject, processAriaObject } from '../../utils/aria-object';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../utils/generate-unique-uuid';
import { genericRenderLabel } from '../../utils/generic-render-label';
import {
	onGenericSubInputBlur,
	onGenericSubInputFocus,
	onGenericSubInputInput,
} from '../../utils/generic-sub-input-events';
import {
	ValidationEvent,
	ValidationFunction,
	ValidationMessages,
	ValidationState,
	getValidationCustomMessages,
	getValidationState,
	validate,
} from '../../utils/input-validation';
import { hasSlotted } from '../../utils/slotted';

@Component({
	shadow: true,
	styleUrl: 'zds-switch.scss',
	tag: 'zds-switch',
})
export class ZdsSwitch implements ZdsInputElement {
	inputElement: HTMLInputElement;
	hasLoaded: boolean;
	validationMessages: ValidationMessages;
	validationFunctions: ValidationFunction[] = [() => getValidationState(this.inputElement.validity)];

	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter;
	@Event() zdsFocus: EventEmitter;
	@Event({ bubbles: true }) zdsChange: EventEmitter;
	@Event({ bubbles: true }) zdsInput: EventEmitter;
	@Event({ bubbles: true }) zdsInputDidUpdate: EventEmitter;
	@Event() zdsValidate: EventEmitter<ValidationEvent>;

	@Event({ bubbles: true }) zdsFormDoSubmit: EventEmitter;

	@Prop({ reflect: true, mutable: true }) checked: boolean = false;
	@Prop() disabled: boolean;
	@Prop() inputAria: AriaObject | string;
	@Prop() label: string;
	@Prop() name: string;
	@Prop() noAsterisk: boolean = false;
	@Prop({ mutable: true }) value: string;
	@Prop({ reflect: true }) zdsId: string = generateUniqueUuid('zds-switch');

	@State() focused: boolean = false;
	@State() touched: boolean = false;
	@State() validationState: ValidationState = {
		message: '',
		reason: '',
		valid: false,
	};

	@Watch('checked')
	onCheckedChange(newValue: boolean, currentValue: boolean) {
		if (currentValue === newValue) return;
		this.toggle(this.checked, { isUserInput: false });
	}

	@Method()
	async reset() {
		this.checked = false;
		this.touched = false;

		await this.validate();
	}

	@Method()
	async validate(forceTouched?: boolean) {
		this.touched = forceTouched ?? this.touched;

		this.validationState = validate(
			this.validationFunctions,
			this.validationMessages,
			this.value,
			null,
			this.disabled,
			false,
		);

		if (this.validationState.valid) {
			const event = this.zdsValidate.emit({
				value: this.value,
				viewValue: null,
			});
			if (event.detail.validationState) {
				this.validationState = event.detail.validationState;

				if (!this.validationState.valid && this.validationMessages[this.validationState.reason]) {
					this.validationState.message = this.validationMessages[this.validationState.reason];
				}
			}
		}

		return this.validationState.valid;
	}

	@Watch('required')
	onRequiredChange(newValue: boolean) {
		this.inputElement.required = newValue;
		this.validate();
	}

	@Bind
	async onBlur(event: FocusEvent) {
		await this.validate();

		onGenericSubInputBlur(this, event);
	}

	@Bind
	async onChange(event: KeyboardEvent) {
		event.stopPropagation();
		this.toggle(this.inputElement.checked);
	}

	@Bind
	onFocus(event: FocusEvent) {
		onGenericSubInputFocus(this, event);
	}

	@Bind
	onInput(event: InputEvent) {
		onGenericSubInputInput(this, event);
	}

	@Bind
	onKeyDown(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			this.zdsFormDoSubmit.emit();
		}
	}

	connectedCallback(): void {
		this.inputDidUpdate();
	}

	componentDidLoad() {
		// Disabled animations on page load
		setTimeout(() => {
			this.hasLoaded = true;
		}, 1);
	}

	componentWillLoad(): void {
		this.validationMessages = getValidationCustomMessages(this.hostElement);
	}

	componentDidUpdate() {
		this.inputDidUpdate();
	}

	render() {
		const containerClasses = generateCssClasses('choice-input', {
			isChecked: this.checked,
			isDisabled: this.disabled,
			isInvalid: this.touched && !this.validationState.valid,
			hasLoaded: this.hasLoaded,
		});

		return (
			<div class={containerClasses}>
				{genericRenderLabel(this.hostElement, this.label, `${this.zdsId}-input`, {}, false, this.noAsterisk)}
				{this.renderError()}
				{this.renderInput()}
			</div>
		);
	}

	renderError() {
		if (!this.touched || this.validationState.valid) return null;

		return (
			<div class="choice-input__error">
				<zds-form-error aria-live="assertive" role="alert" id={`${this.zdsId}-feedback`}>
					{this.validationState.message}
				</zds-form-error>
			</div>
		);
	}

	renderInlineLabel() {
		if (!hasSlotted(this.hostElement)) return null;

		return (
			<div class="choice-input__label-text">
				<slot />
			</div>
		);
	}

	renderInput() {
		const inputAriaProps = processAriaObject(this.inputAria, 'zds-switch', 'input-aria');

		return (
			<label class="choice-input__label" htmlFor={`${this.zdsId}-input`}>
				<div class="choice-input__input-wrapper">
					<input
						aria-describedby={
							this.touched && !this.validationState.valid ? `${this.zdsId}-feedback` : false
						}
						aria-invalid={!this.validationState.valid}
						checked={this.checked}
						class="choice-input__input-native"
						disabled={this.disabled || undefined}
						id={`${this.zdsId}-input`}
						name={this.name}
						onBlur={(event) => void this.onBlur(event)}
						onChange={(event: KeyboardEvent) => void this.onChange(event)}
						onFocus={this.onFocus}
						onInput={this.onInput}
						onKeyDown={this.onKeyDown}
						ref={(element) => (this.inputElement = element)}
						type="checkbox"
						value={this.value}
						{...inputAriaProps}
					/>
					<div class="choice-input__input-graphic">
						<div class="choice-input__input-graphic-dot"></div>
					</div>
				</div>
				{this.renderInlineLabel()}
			</label>
		);
	}

	inputDidUpdate() {
		this.zdsInputDidUpdate.emit({
			id: this.zdsId,
			includeInForm: !!this.checked,
		});
	}

	async toggle(
		checked: boolean,
		{ isUserInput = true, shiftKey = false }: { shiftKey?: boolean; isUserInput?: boolean } = {},
	) {
		if (this.checked === checked) {
			return;
		}

		this.checked = this.inputElement.checked = checked;
		this.value = this.inputElement.value;
		this.zdsChange.emit({ shiftKey });

		if (isUserInput) {
			this.touched = true;
		}

		await this.validate();
	}
}
